import {Component, OnInit, Input, forwardRef, ViewChild} from '@angular/core'
import {
  FormGroup,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms'

import {Observable, of, BehaviorSubject} from 'rxjs'
import {
  distinctUntilChanged,
  switchMap,
  debounceTime,
  tap,
  catchError,
  map,
} from 'rxjs/operators'
import {NgSelectComponent} from '@ng-select/ng-select'

import {DomainService, Domain} from '@app/core/services/rest-api'
import {LoggerService} from '@app/core/services/logger.service'
import {BsModalService} from 'ngx-bootstrap/modal'
import {DomainSelectorNewModalComponent} from './domain-selector-new-modal.component'

@Component({
  selector: 'app-domain-selector, domain-selector',
  templateUrl: './domain-selector.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DomainSelectorComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class DomainSelectorComponent implements OnInit, ControlValueAccessor {
  @ViewChild(NgSelectComponent, {static: true}) select: NgSelectComponent

  @Input() placeholder = ''
  @Input() formGroup: FormGroup
  @Input() allowNewDomain: boolean = false
  @Input() readonly = false

  public domainList: Observable<Domain[]>
  public typeahead = new BehaviorSubject<string>(null)
  public newDomainCode = new BehaviorSubject<string>(null)
  public domainListLoading = false
  public loadResultsTotal: number
  public loadResultsCount: number
  public domain: Domain
  _onChanged: any

  constructor(
    private domainSvc: DomainService,
    private modalService: BsModalService,
    private logger: LoggerService,
  ) {}

  ngOnInit() {
    /* On typeahead debnounce and make unique */
    /* Then search in backend */
    this.domainList = this.typeahead.pipe(
      debounceTime(100),
      distinctUntilChanged(),
      tap(() => (this.domainListLoading = true)),
      switchMap((term) => this.searchDomains(term)),
      map((allDomains) => allDomains.filter((domain) => domain.active)), //remove inactive domains
      tap((domains) => {
        /* If we have a code and its in the domain list then return null */
        /* else we may want to create a new domain with this code so return the code */
        if (
          !this.allowNewDomain ||
          (this.typeahead.value &&
            domains.find((d) => d.code == this.typeahead.value))
        ) {
          this.newDomainCode.next(null)
        } else {
          this.newDomainCode.next(this.typeahead.value)
        }
      }),
      tap(() => (this.domainListLoading = false)),
    )
  }

  /* Search for domains given a code string */
  private searchDomains(value: any): Observable<any> {
    var search
    if (value) {
      search = '+code:' + value + '*'
    } else {
      search = ''
    }

    return this.domainSvc.getDomains({search: search, results: 50}).pipe(
      map((res) => {
        this.loadResultsTotal = res.count
        this.loadResultsCount = res.results.length
        return res.results
      }),
      catchError((err) => of([])),
    )
  }

  /* Get a specific domain by code */
  private getDomain(code: any): Observable<any> {
    if (code == null) {
      return of(null)
    }
    return this.domainSvc.getDomain(code).pipe(catchError((err) => of(null)))
  }

  onOpen() {
    /* Work around typeahead not being cleared on new open */
    this.typeahead.next(null)
  }

  /* On selecting a new domain from list fire the change listener */
  onChange(domain) {
    if (this._onChanged) {
      if (domain) {
        this._onChanged(domain.code)
      } else {
        this._onChanged(null)
      }
    }
  }

  /* New value writtern from form. Lookup the domain for the code and set as value */
  writeValue(obj: any) {
    if (obj) {
      this.domainListLoading = true
      this.getDomain(obj)
        .pipe(
          tap((domain) => {
            if (domain) {
              this.select.writeValue(domain)
            } else {
              this.select.writeValue(null)
            }
            this.onChange(domain)
            this.domainListLoading = false
          }),
        )
        .subscribe()
    }
  }

  registerOnChange(fn: any) {
    this._onChanged = fn
    //this.select.registerOnChange(fn);
  }

  registerOnTouched(fn: any) {
    this.select.registerOnTouched(fn)
  }

  openCreateDomainModal() {
    this.select.close()
    this.modalService.show(DomainSelectorNewModalComponent, {
      class: 'modal-lg',
      initialState: {
        initialCode: this.newDomainCode.value,
        onComplete: (val) => this.writeValue(val),
      },
    })
  }

  showCreateNewDomain() {
    return this.typeahead.getValue() != undefined
  }
}
